import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState({ checking: true, email: "", invalid: "" });
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false); const [error, setError] = useState("");
  useEffect(() => {
    if (!token) { setState({ checking: false, invalid: "Missing reset token." }); return; }
    api.get(`/auth/reset-password/${encodeURIComponent(token)}`).then((r) => setState({ checking: false, email: r.data.email })).catch((e) => setState({ checking: false, invalid: formatApiErrorDetail(e.response?.data?.detail) }));
  }, [token]);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters");
    if (pw !== pw2) return setError("Passwords do not match");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, new_password: pw }); const me = await api.get("/auth/me"); setUser(me.data); navigate("/dashboard"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-[#0b1a3a] p-4" data-testid="reset-page">
      <Card className="w-full max-w-md p-8 space-y-6">
        <div className="flex items-center gap-3"><div className="bg-white rounded-lg p-1.5 border"><img src="/rightpay-logo.png" alt="RightPay" className="h-8" /></div><div><h1 className="font-display text-xl font-bold">Choose a new password</h1>{state.email && <p className="text-xs text-muted-foreground" data-testid="reset-email">for {state.email}</p>}</div></div>
        {state.checking ? <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking your link…</p> : state.invalid ? (
          <div className="space-y-4" data-testid="reset-invalid"><p className="text-sm text-destructive">{state.invalid}</p><Button asChild className="w-full"><Link to="/forgot-password">Request a new link</Link></Button></div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5"><Label htmlFor="rp-pw">New password</Label><Input id="rp-pw" data-testid="reset-password" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" /></div>
            <div className="space-y-1.5"><Label htmlFor="rp-pw2">Confirm new password</Label><Input id="rp-pw2" data-testid="reset-password-confirm" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" /></div>
            {error && <p data-testid="reset-error" className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" data-testid="reset-submit" disabled={busy}>{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />} Set password &amp; sign in</Button>
          </form>
        )}
      </Card>
    </div>
  );
}
